import type { Reference } from '@peruselab/api-client/discussions';

/**
 * ADR 0016 D21 (#910): does the text state a value -- a number with a unit ("mp 135 °C", "pKa 3.5",
 * "IC50 50 nM", "δ 7.26 ppm", "95%")? Then the composer asks "Add a source or data?" (it never
 * blocks posting). Deliberately simple: a nudge, not a classifier.
 */
const VALUE_WITH_UNIT =
  /\d(?:[.,]\d+)?\s*(?:±\s*\d+(?:[.,]\d+)?\s*)?(?:°\s?[CFK]|K\b|%|ppm|nm|µm|um|mm|cm|m\b|mg|µg|ug|g\b|kg|mL|µL|L\b|mol|mmol|µM|uM|nM|mM|M\b|Da|kDa|g\/mol|kJ|kcal|J\b|eV|Hz|MHz|bar|Pa|kPa|atm|torr|min|h\b|s\b|cm-1|cm⁻¹)/i;
const NAMED_VALUE = /\b(?:pKa|pKb|pH|logP|log ?P|IC50|EC50|Ki|Kd|LD50|mp|bp|Rf)\b\s*[:=≈~]?\s*-?\d/i;

export function statesAValue(text: string): boolean {
  return VALUE_WITH_UNIT.test(text) || NAMED_VALUE.test(text);
}

/** "McNeil, J.J., Wolfe, R. and 2 others" -- the first few authors, then a count. */
export function formatAuthors(authors: readonly string[], shown = 3): { names: string; more: number } {
  return { names: authors.slice(0, shown).join('; '), more: Math.max(0, authors.length - shown) };
}

/** The labels a reference carries (D20, D22); an accepted, sound reference carries none. */
export function referenceLabels(
  reference: Reference
): readonly ('preprint' | 'web' | 'data' | 'retracted' | 'linkUnavailable')[] {
  const labels: ('preprint' | 'web' | 'data' | 'retracted' | 'linkUnavailable')[] = [];
  if (reference.integrity === 'RETRACTED') labels.push('retracted');
  if (reference.integrity === 'LINK_UNAVAILABLE') labels.push('linkUnavailable');
  if (reference.standing === 'PREPRINT') labels.push('preprint');
  if (reference.standing === 'WEB') labels.push('web');
  if (reference.standing === 'PERUSELAB') labels.push('data');
  return labels;
}
